import type { Bundle, BundleKey } from './validate';

/** Deep equality for JSON values, treating a missing key, `undefined`, and `null` as the same. */
export function jsonEqual(a: unknown, b: unknown): boolean {
  if (a == null || b == null) return a == null && b == null;
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
    return a.every((x, i) => jsonEqual(x, b[i]));
  }
  if (typeof a === 'object' && typeof b === 'object') {
    const ao = a as Record<string, unknown>;
    const bo = b as Record<string, unknown>;
    const keys = new Set([...Object.keys(ao), ...Object.keys(bo)]);
    for (const k of keys) if (!jsonEqual(ao[k], bo[k])) return false;
    return true;
  }
  return a === b;
}

/** Dotted paths of the leaves that differ between two JSON values (arrays of scalars count as one leaf). */
export function changedPaths(a: unknown, b: unknown, prefix = ''): string[] {
  if (jsonEqual(a, b)) return [];
  const isObj = (v: unknown) => v != null && typeof v === 'object' && !Array.isArray(v);
  if (isObj(a) && isObj(b)) {
    const ao = a as Record<string, unknown>;
    const bo = b as Record<string, unknown>;
    const keys = [...new Set([...Object.keys(ao), ...Object.keys(bo)])];
    return keys.flatMap((k) => changedPaths(ao[k], bo[k], prefix ? `${prefix}.${k}` : k));
  }
  if (
    Array.isArray(a) &&
    Array.isArray(b) &&
    a.length === b.length &&
    a.some((x) => x != null && typeof x === 'object')
  ) {
    return a.flatMap((x, i) => changedPaths(x, b[i], prefix ? `${prefix}.${i}` : String(i)));
  }
  return [prefix || '(value)'];
}

export type CollectionDiff = {
  kind: 'collection';
  section: BundleKey;
  added: string[];
  removed: string[];
  changed: { id: string; paths: string[] }[];
  reordered: boolean;
};

export type SectionDiff = { kind: 'section'; section: BundleKey; paths: string[] };

export type BundleDiff = (CollectionDiff | SectionDiff)[];

/** A per-entry summary of what the draft changes compared with a published bundle. Unchanged sections are left out. */
export function diffBundles(published: Bundle, draft: Bundle): BundleDiff {
  const out: BundleDiff = [];
  const keys = [...new Set([...Object.keys(published), ...Object.keys(draft)])].filter(
    (k) => k !== 'version',
  ) as BundleKey[];
  for (const key of keys) {
    const a = published[key];
    const b = draft[key];
    if (Array.isArray(a) || Array.isArray(b)) {
      const before = ((a ?? []) as { id?: string }[]).map((e, i) => [e.id ?? `#${i}`, e] as const);
      const after = ((b ?? []) as { id?: string }[]).map((e, i) => [e.id ?? `#${i}`, e] as const);
      const beforeMap = new Map(before);
      const afterMap = new Map(after);
      const added = after.filter(([id]) => !beforeMap.has(id)).map(([id]) => id);
      const removed = before.filter(([id]) => !afterMap.has(id)).map(([id]) => id);
      const changed = after
        .filter(([id, e]) => beforeMap.has(id) && !jsonEqual(beforeMap.get(id), e))
        .map(([id, e]) => ({ id, paths: changedPaths(beforeMap.get(id), e) }));
      const keptBefore = before.map(([id]) => id).filter((id) => afterMap.has(id));
      const keptAfter = after.map(([id]) => id).filter((id) => beforeMap.has(id));
      const reordered = keptBefore.some((id, i) => keptAfter[i] !== id);
      if (added.length || removed.length || changed.length || reordered)
        out.push({ kind: 'collection', section: key, added, removed, changed, reordered });
    } else {
      const paths = changedPaths(a, b);
      if (paths.length) out.push({ kind: 'section', section: key, paths });
    }
  }
  return out;
}
